/**
 * Edge Function Client
 *
 * In development:
 *   - Auth0-related functions use the Vite dev middleware (/dev-api/*)
 *     which calls Auth0 Management API server-side (no CORS, no edge function).
 *   - Other edge functions use the Vite proxy (/supafunc/*).
 *
 * In production:
 *   - All calls go directly to Supabase edge functions.
 *
 * @module edgeFunctionClient
 */

import { createLogger } from './logger'

const log = createLogger('EdgeFunctionClient')

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_KEY = (
  (import.meta.env as any).VITE_SUPABASE_SERVICE_ROLE_KEY ||
  (import.meta.env as any).VITE_SUPABASE_ANON_KEY
) as string

/**
 * Dev-only routes that bypass Supabase edge functions entirely.
 * Maps edge function names to local Vite middleware endpoints.
 */
const DEV_LOCAL_ROUTES: Record<string, string> = {
  'auth0-management-users': '/dev-api/auth0-users',
}

/**
 * Invoke a Supabase Edge Function (or dev-local equivalent).
 *
 * @param fnName - The edge function slug (e.g. 'auth0-management-users')
 * @param body   - JSON body to POST
 * @returns {{ data: T | null; error: string | null }}
 */
export async function invokeEdgeFunction<T = unknown>(
  fnName: string,
  body: Record<string, unknown>,
): Promise<{ data: T | null; error: string | null }> {
  const isDev = import.meta.env.DEV

  let url: string
  let headers: Record<string, string>

  if (isDev && DEV_LOCAL_ROUTES[fnName]) {
    // Use Vite dev middleware — runs entirely in Node.js, no CORS
    url = DEV_LOCAL_ROUTES[fnName]
    headers = { 'Content-Type': 'application/json' }
    log.debug(`Invoking ${fnName} (via dev middleware: ${url})`)
  } else if (isDev) {
    // Use Vite proxy for other edge functions
    url = `/supafunc/${fnName}`
    headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_KEY}`,
      'apikey': SUPABASE_KEY,
    }
    log.debug(`Invoking ${fnName} (via proxy)`)
  } else {
    // Production — call Supabase directly
    url = `${SUPABASE_URL}/functions/v1/${fnName}`
    headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_KEY}`,
      'apikey': SUPABASE_KEY,
    }
    log.debug(`Invoking ${fnName} (direct)`)
  }

  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })

    if (!resp.ok) {
      const text = await resp.text()
      log.error(`${fnName} failed: ${resp.status}`, text)
      return { data: null, error: `${resp.status}: ${text}` }
    }

    const data = (await resp.json()) as T
    return { data, error: null }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    log.error(`${fnName} network error:`, msg)
    return { data: null, error: msg }
  }
}
